import { paths } from '@/types/api';
import { ApiError, fetchClient } from '@/util/fetchClient';

type PostInquiryDraftV2Request =
  paths['/v2/drafts']['post']['requestBody']['content']['application/json'];

const postInquiryDraftV2 = async (payload: PostInquiryDraftV2Request) => {
  const { data } = await fetchClient.POST('/v2/drafts', {
    body: payload,
  });

  return data?.data;
};

type PutInquiryDraftV2Request =
  paths['/v2/drafts/{draftUuid}']['put']['requestBody']['content']['application/json'];

const putInquiryDraftV2 = async (draftUuid: string, payload: PutInquiryDraftV2Request) => {
  const { data } = await fetchClient.PUT(`/v2/drafts/{draftUuid}`, {
    params: {
      path: {
        draftUuid,
      },
    },
    body: payload,
  });

  return data?.data;
};

type DraftPayload = {
  cancerFocusId?: string;
  message: string;
  noPII: boolean;
  anonymous?: boolean;
  draftUuid?: string;
  expertIds: number[] | null;
  expertId?: number | null;
  /**
   * Asked when the draft turns out to be gone. Answer false when this screen deleted it or
   * submitted the inquiry itself, so a save already in flight does not bring it back.
   */
  shouldRecreateIfMissing?: () => boolean;
};

/**
 * Creates the draft, or saves over it when it has an id.
 *
 * A draft can disappear while its form is open: deleted or submitted in another tab or on
 * another device, since submitting deletes it. The API answers that save with a 404, and the
 * form's text is then saved as a new draft rather than dropped. The caller adopts the returned
 * uuid, so later saves follow the new draft. When the caller says not to, the save resolves
 * with nothing.
 */
export const setInquiryDraftV2 = async (draft: DraftPayload) => {
  const payload: PostInquiryDraftV2Request = {
    expertIds: draft.expertIds ?? null,
    expertId: draft.expertId ?? null,
    cancerTypeId: draft.cancerFocusId ? Number(draft.cancerFocusId) : null,
    message: draft.message,
    noPII: draft.noPII,
    anonymous: draft.anonymous ?? null,
    diagnosisStatus: null,
    age: null,
  };

  if (!draft.draftUuid) {
    return postInquiryDraftV2(payload);
  }

  try {
    return await putInquiryDraftV2(draft.draftUuid, payload as PutInquiryDraftV2Request);
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) {
      return (draft.shouldRecreateIfMissing?.() ?? true) ? postInquiryDraftV2(payload) : undefined;
    }

    throw error;
  }
};
